import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { Selector } from '@/components/ui/inputs';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';
import { useTranslation } from '@/i18n';
import { useManagedAttendance } from '@/hooks/attendance/myAttendance/useManagedAttendance';
import type { ManagedAttendanceAction, ManagedAttendanceFilters, ManagedAttendanceStatus } from '@/api/domains/attendance';


export const ManagedAttendance: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    data,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    actions,
    statusFilterOptions,
    actionFilterOptions,
  } = useManagedAttendance();

  // Inline filters component - simple enough to include in page
  const renderFilters = () => (
    <div className="flex flex-col gap-3 pt-2">
      <Selector
        label={translate('attendance:managedAttendance.filters.status')}
        value={filters.status || ''}
        options={statusFilterOptions}
        onChange={(value) => {
          if (typeof value === 'string') {
            setFilters((previous: ManagedAttendanceFilters) => ({
              ...previous,
              status: value as ManagedAttendanceStatus | 'all',
            }));
          }
        }}
        data-testid="status-filter"
      />
      <Selector
        label={translate('attendance:managedAttendance.filters.action')}
        value={filters.action || ''}
        options={actionFilterOptions}
        onChange={(value) => {
          if (typeof value === 'string') {
            setFilters((previous: ManagedAttendanceFilters) => ({
              ...previous,
              action: value as ManagedAttendanceAction | 'all',
            }));
          }
        }}
        data-testid="action-filter"
      />
    </div>
  );

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="managed-attendance-page">
        <div className="flex flex-wrap gap-3 items-center">
          <DateRangeFilter
            value={{ from: filters.from, to: filters.to }}
            defaultValue={{ from: defaultFrom, to: defaultTo }}
            onChange={(range) => {
              setFilters((previous: ManagedAttendanceFilters) => ({
                ...previous,
                from: range.from || '',
                to: range.to || '',
              }));
            }}
            maxDate={formatDateToYYYYMMDD(new Date())}
            minDate={getMinDateByMonths()}
            closeOnComplete
          />
          <FilterDropdown
            values={filters as unknown as Record<string, string | string[] | undefined>}
            extraFilters={renderFilters()}
          />
        </div>
        <Table
          columns={columns}
          data={data}
          totalRows={totalRecords}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total: totalRecords,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'creationDate', direction: 'desc' }}
          data-testid="table-managed-attendance"
          actions={actions}
        />
      </div>
    </PageLayout>
  );
};

export default ManagedAttendance;
